import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import Paper from 'components/Paper';
import Images from 'assets/img';
import Button from 'components/Button';
import TripActions from 'store/ducks/trip';
import TruckerActions from 'store/ducks/trucker';
import ProvidersActions from 'store/ducks/providers';
import { sefazParamsAPI, clientesAPI, embarcadoresAPI } from 'services/apiRest';
import ModalClienteNaoCadastrado from '../components/ModalClienteNaoCadastrado';
import ViagemForm from '../components/ViagemForm';
import ImportarXmlViagem from '../components/ImportarXmlViagem';
import { Container } from '../components/ModalViagemCompleto/styles';

function CreateTrip() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const { truckers, viagens, providers } = useSelector((state) => ({
    truckers: state.trucker.truckers,
    viagens: state.trip.viagens,
    providers: state.providers.providers,
  }));
  const signedIn = useSelector((state) => state.auth.signedIn);

  // logs de debug removidos

  const isEdit = !!id;
  const [activeTab, setActiveTab] = useState(0);
  const [ambienteSefaz, setAmbienteSefaz] = useState('homologacao');
  const formikRef = useRef(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [cpfCnpjTomador, setCpfCnpjTomador] = useState('');
  const [dadosTomador, setDadosTomador] = useState(null);
  const [embarcadores, setEmbarcadores] = useState([]);

  // Buscar viagem se estiver editando
  useEffect(() => {
    if (isEdit && viagens.length === 0) {
      dispatch(TripActions.getViagensRequest());
    }
  }, [isEdit, id, dispatch, viagens.length]);

  useEffect(() => {
    // Só carregar truckers e providers se o usuário estiver autenticado
    if (signedIn) {
      dispatch(TruckerActions.getTruckersRequest());
      dispatch(ProvidersActions.getProvidersRequest());
    }
  }, [dispatch, signedIn]);

  useEffect(() => {
    if (!signedIn) return;
    embarcadoresAPI
      .listar()
      .then((list) => setEmbarcadores(Array.isArray(list) ? list : []))
      .catch((e) => {
        console.error('[CreateTrip] Erro ao carregar embarcadores:', e);
        setEmbarcadores([]);
      });
  }, [signedIn]);

  // Carregar parâmetros SEFAZ para obter ambiente
  useEffect(() => {
    const carregarParamsSefaz = async () => {
      try {
        const params = await sefazParamsAPI.buscarPorTipo('CTE');
        if (params && params.ambiente) {
          setAmbienteSefaz(params.ambiente);
        }
      } catch (error) {
        console.error('Erro ao carregar parâmetros SEFAZ:', error);
        // Manter padrão se houver erro
      }
    };
    
    if (!isEdit) {
      // Só carregar se não estiver editando (ao editar, usa o ambiente da viagem)
      carregarParamsSefaz();
    }
  }, [isEdit]);

  const viagem = isEdit ? viagens.find(v => v.id === id) : null;

  // Carregar ambiente dos parâmetros SEFAZ ou da viagem (se editando)
  const getPersistedAmbiente = () => {
    if (viagem?.ambiente) {
      return viagem.ambiente; // Se estiver editando, usa o valor da viagem
    }
    // Se não estiver editando, usa o ambiente dos parâmetros SEFAZ
    return ambienteSefaz;
  };

  // Usar useMemo para evitar recálculo desnecessário dos initialValues
  // Isso evita que o enableReinitialize resete os valores quando não deveria
  const initialValues = useMemo(() => ({
    ambiente: getPersistedAmbiente(),
    referencia: viagem?.referencia || '',
    data_emissao: viagem?.data_emissao || new Date().toISOString().split('T')[0],
    tipo_cte: viagem?.tipo_cte || '0',
    natureza_operacao: viagem?.natureza_operacao || '',
    tipo_servico: viagem?.tipo_servico || '0',
    modal: viagem?.modal || '01',
    cfop: viagem?.cfop || '',
    
    emitente_cpf_cnpj: viagem?.emitente?.cpf_cnpj || '',
    emitente_nome_razao_social: viagem?.emitente?.nome_razao_social || '',
    emitente_nome_fantasia: viagem?.emitente?.nome_fantasia || '',
    emitente_inscricao_estadual: viagem?.emitente?.inscricao_estadual || '',
    emitente_inscricao_municipal: viagem?.emitente?.inscricao_municipal || '',
    emitente_cep: viagem?.emitente?.endereco?.cep || '',
    emitente_logradouro: viagem?.emitente?.endereco?.logradouro || '',
    emitente_numero: viagem?.emitente?.endereco?.numero || '',
    emitente_complemento: viagem?.emitente?.endereco?.complemento || '',
    emitente_bairro: viagem?.emitente?.endereco?.bairro || '',
    emitente_uf: viagem?.emitente?.endereco?.uf || '',
    emitente_municipio_codigo: viagem?.emitente?.endereco?.municipio?.codigo_ibge || '',
    emitente_municipio_descricao: viagem?.emitente?.endereco?.municipio?.descricao || '',
    emitente_telefone_ddd: viagem?.emitente?.telefones?.[0]?.ddd || '',
    emitente_telefone_numero: viagem?.emitente?.telefones?.[0]?.numero || '',
    emitente_email: viagem?.emitente?.email || '',
    emitente_rntrc: viagem?.rntrc || '',
    
    remetente_cpf_cnpj: viagem?.remetente?.cpf_cnpj || '',
    remetente_nome_razao_social: viagem?.remetente?.nome_razao_social || '',
    remetente_inscricao_estadual: viagem?.remetente?.inscricao_estadual || '',
    remetente_cep: viagem?.remetente?.endereco?.cep || '',
    remetente_logradouro: viagem?.remetente?.endereco?.logradouro || '',
    remetente_numero: viagem?.remetente?.endereco?.numero || '',
    remetente_complemento: viagem?.remetente?.endereco?.complemento || '',
    remetente_bairro: viagem?.remetente?.endereco?.bairro || '',
    remetente_uf: viagem?.remetente?.endereco?.uf || '',
    remetente_municipio_codigo: viagem?.remetente?.endereco?.municipio?.codigo_ibge || '',
    remetente_municipio_descricao: viagem?.remetente?.endereco?.municipio?.descricao || '',
    remetente_telefone_ddd: viagem?.remetente?.telefones?.[0]?.ddd || '',
    remetente_telefone_numero: viagem?.remetente?.telefones?.[0]?.numero || '',
    remetente_email: viagem?.remetente?.email || '',
    remetente_lat: viagem?.remetente?.lat || viagem?.remetente_lat || '',
    remetente_lng: viagem?.remetente?.lng || viagem?.remetente_lng || '',
    
    destinatario_cpf_cnpj: viagem?.destinatario?.cpf_cnpj || '',
    destinatario_nome_razao_social: viagem?.destinatario?.nome_razao_social || '',
    destinatario_inscricao_estadual: viagem?.destinatario?.inscricao_estadual || '',
    destinatario_cep: viagem?.destinatario?.endereco?.cep || '',
    destinatario_logradouro: viagem?.destinatario?.endereco?.logradouro || '',
    destinatario_numero: viagem?.destinatario?.endereco?.numero || '',
    destinatario_complemento: viagem?.destinatario?.endereco?.complemento || '',
    destinatario_bairro: viagem?.destinatario?.endereco?.bairro || '',
    destinatario_uf: viagem?.destinatario?.endereco?.uf || '',
    destinatario_municipio_codigo: viagem?.destinatario?.endereco?.municipio?.codigo_ibge || '',
    destinatario_municipio_descricao: viagem?.destinatario?.endereco?.municipio?.descricao || '',
    destinatario_telefone_ddd: viagem?.destinatario?.telefones?.[0]?.ddd || '',
    destinatario_telefone_numero: viagem?.destinatario?.telefones?.[0]?.numero || '',
    destinatario_email: viagem?.destinatario?.email || '',
    destinatario_lat: viagem?.destinatario?.lat || viagem?.destinatario_lat || '',
    destinatario_lng: viagem?.destinatario?.lng || viagem?.destinatario_lng || '',
    
    tomador_tipo: viagem?.tomador?.tipo || '0',
    tomador_cpf_cnpj: viagem?.tomador?.cpf_cnpj || '',
    tomador_nome_razao_social: viagem?.tomador?.nome_razao_social || '',
    tomador_inscricao_estadual: viagem?.tomador?.inscricao_estadual || '',
    tomador_cep: viagem?.tomador?.endereco?.cep || '',
    tomador_logradouro: viagem?.tomador?.endereco?.logradouro || '',
    tomador_numero: viagem?.tomador?.endereco?.numero || '',
    tomador_complemento: viagem?.tomador?.endereco?.complemento || '',
    tomador_bairro: viagem?.tomador?.endereco?.bairro || '',
    tomador_uf: viagem?.tomador?.endereco?.uf || '',
    tomador_municipio_codigo: viagem?.tomador?.endereco?.municipio?.codigo_ibge || '',
    tomador_municipio_descricao: viagem?.tomador?.endereco?.municipio?.descricao || '',
    tomador_telefone_ddd: viagem?.tomador?.telefones?.[0]?.ddd || '',
    tomador_telefone_numero: viagem?.tomador?.telefones?.[0]?.numero || '',
    tomador_email: viagem?.tomador?.email || '',
    
    valor_servico: viagem?.transporte?.valor_servico || '',
    valor_receber: viagem?.transporte?.valor_receber || '',
    valor_carga: viagem?.transporte?.valor_carga || '',
    produtos_predominantes: viagem?.transporte?.produtos_predominantes || '',
    chave_nfe: viagem?.chave_nfe || '',
    outras_caracteristicas: viagem?.transporte?.outras_caracteristicas || '',
    
    veiculo_placa: viagem?.veiculo?.placa || '',
    veiculo_renavam: viagem?.veiculo?.renavam || '',
    veiculo_uf: viagem?.veiculo?.uf || '',
    veiculo_proprietario_cpf_cnpj: viagem?.veiculo?.proprietario?.cpf_cnpj || '',
    veiculo_proprietario_nome: viagem?.veiculo?.proprietario?.nome_razao_social || '',
    veiculo_proprietario_inscricao_estadual: viagem?.veiculo?.proprietario?.inscricao_estadual || '',
    veiculo_proprietario_uf: viagem?.veiculo?.proprietario?.uf || '',
    
    motorista_id: viagem?.motorista_id || '',
    motorista_nome: viagem?.motorista_nome || '',
    motorista_cpf: viagem?.motorista_cpf || '',
    motorista_telefone_ddd: viagem?.motorista_telefone_ddd || '',
    motorista_telefone_numero: viagem?.motorista_telefone_numero || '',
    
    informacoes_adicionais: viagem?.informacoes_adicionais || '',
    observacoes: viagem?.observacoes || '',

    // Embarcador
    embarcador_id: viagem?.embarcador_id || '',

  }), [viagem]);

  // Função helper para remover máscara de CPF/CNPJ (remove pontos, traços, barras)
  const removerMascaraCpfCnpj = (valor) => {
    if (!valor) return '';
    // Remove tudo exceto números
    return valor.toString().replace(/\D/g, '');
  };

  // Função helper para remover máscara de CEP (remove traços)
  const removerMascaraCep = (valor) => {
    if (!valor) return '';
    // Remove tudo exceto números
    return valor.toString().replace(/\D/g, '');
  };

  const handleSubmit = async (values) => {
    // Validar se tomador está cadastrado como cliente ANTES de continuar
    // Pegar o CNPJ/CPF do campo tomador_cpf_cnpj
    const cpfCnpjTomador = values.tomador_cpf_cnpj ? values.tomador_cpf_cnpj.replace(/\D/g, '') : '';
    
    if (!cpfCnpjTomador || cpfCnpjTomador.length === 0) {
      setErrorMessage('O CPF/CNPJ do tomador é obrigatório. Por favor, preencha o campo CPF/CNPJ do tomador.');
      setShowErrorModal(true);
      return; // Bloqueia o salvamento
    }
    
    // Validar se o CPF/CNPJ tem tamanho válido (11 para CPF ou 14 para CNPJ)
    if (cpfCnpjTomador.length !== 11 && cpfCnpjTomador.length !== 14) {
      setErrorMessage('CPF/CNPJ do tomador inválido. Por favor, verifique o número informado.');
      setShowErrorModal(true);
      return; // Bloqueia o salvamento
    }
    
    // Buscar o cliente no cadastro
    try {
      const clientes = await clientesAPI.listar({ cpf_cnpj: cpfCnpjTomador });
      
      if (!clientes || clientes.length === 0) {
        // Tomador não está cadastrado como cliente
        const cpfCnpjFormatado = cpfCnpjTomador.length === 11 
          ? cpfCnpjTomador.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
          : cpfCnpjTomador.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        // tomador não encontrado
        
        // Preparar dados do tomador para pré-preencher o formulário de cliente
        const dadosTomadorParaPreencher = {
          cpfCnpj: cpfCnpjFormatado,
          nome: values.tomador_nome_razao_social || '',
          nomeFantasia: '',
          tipoPessoa: cpfCnpjTomador.length === 11 ? 'PF' : 'PJ',
          endereco: values.tomador_logradouro || '',
          numero: values.tomador_numero || '',
          complemento: values.tomador_complemento || '',
          bairro: values.tomador_bairro || '',
          cidade: values.tomador_municipio_descricao || '',
          uf: values.tomador_uf || '',
          cep: values.tomador_cep || '',
          email: values.tomador_email || '',
        };
        
        setCpfCnpjTomador(cpfCnpjFormatado);
        setDadosTomador(dadosTomadorParaPreencher);
        setErrorMessage('O tomador não está cadastrado como cliente. Deseja cadastrá-lo agora?');
        setShowErrorModal(true);
        return; // Bloqueia o salvamento
      }
    } catch (error) {
      console.error('[CreateTrip] Erro ao validar tomador:', error);
      setErrorMessage('Erro ao validar tomador. Tente novamente.');
      setShowErrorModal(true);
      return; // Bloqueia o salvamento
    }

    // Mesma estrutura do ModalViagemCompleto
    const dadosCompletos = {
      ambiente: values.ambiente,
      referencia: values.referencia,
      data_emissao: values.data_emissao,
      tipo_cte: values.tipo_cte,
      natureza_operacao: values.natureza_operacao,
      tipo_servico: values.tipo_servico,
      modal: values.modal,
      
      informacoes_gerais: {
        tipo_servico: values.tipo_servico,
        cfop: values.cfop,
        natureza_operacao: values.natureza_operacao,
      },
      
      emitente: {
        cpf_cnpj: removerMascaraCpfCnpj(values.emitente_cpf_cnpj),
        nome_razao_social: values.emitente_nome_razao_social,
        nome_fantasia: values.emitente_nome_fantasia,
        inscricao_estadual: values.emitente_inscricao_estadual,
        inscricao_municipal: values.emitente_inscricao_municipal,
        endereco: {
          logradouro: values.emitente_logradouro,
          numero: values.emitente_numero,
          complemento: values.emitente_complemento,
          bairro: values.emitente_bairro,
          cep: removerMascaraCep(values.emitente_cep),
          uf: values.emitente_uf,
          municipio: {
            codigo_ibge: values.emitente_municipio_codigo,
            descricao: values.emitente_municipio_descricao,
          },
          pais: {
            codigo: '1058',
            descricao: 'Brasil',
          },
        },
        telefones: values.emitente_telefone_ddd ? [{
          ddd: values.emitente_telefone_ddd,
          numero: values.emitente_telefone_numero,
        }] : [],
        email: values.emitente_email,
      },
      
      rntrc: values.emitente_rntrc,
      
      remetente: {
        cpf_cnpj: removerMascaraCpfCnpj(values.remetente_cpf_cnpj),
        nome_razao_social: values.remetente_nome_razao_social,
        inscricao_estadual: values.remetente_inscricao_estadual,
        endereco: {
          logradouro: values.remetente_logradouro,
          numero: values.remetente_numero,
          complemento: values.remetente_complemento,
          bairro: values.remetente_bairro,
          cep: removerMascaraCep(values.remetente_cep),
          uf: values.remetente_uf,
          municipio: {
            codigo_ibge: values.remetente_municipio_codigo,
            descricao: values.remetente_municipio_descricao,
          },
        },
        telefones: values.remetente_telefone_ddd ? [{
          ddd: values.remetente_telefone_ddd,
          numero: values.remetente_telefone_numero,
        }] : [],
        email: values.remetente_email,
        lat: values.remetente_lat ? parseFloat(values.remetente_lat) : null,
        lng: values.remetente_lng ? parseFloat(values.remetente_lng) : null,
      },
      
      // Campos planos para o backend (remetente_lat e remetente_lng)
      remetente_lat: values.remetente_lat && values.remetente_lat !== '' ? parseFloat(values.remetente_lat) : null,
      remetente_lng: values.remetente_lng && values.remetente_lng !== '' ? parseFloat(values.remetente_lng) : null,
      
      destinatario: {
        cpf_cnpj: removerMascaraCpfCnpj(values.destinatario_cpf_cnpj),
        nome_razao_social: values.destinatario_nome_razao_social,
        inscricao_estadual: values.destinatario_inscricao_estadual,
        endereco: {
          logradouro: values.destinatario_logradouro,
          numero: values.destinatario_numero,
          complemento: values.destinatario_complemento,
          bairro: values.destinatario_bairro,
          cep: removerMascaraCep(values.destinatario_cep),
          uf: values.destinatario_uf,
          municipio: {
            codigo_ibge: values.destinatario_municipio_codigo,
            descricao: values.destinatario_municipio_descricao,
          },
        },
        telefones: values.destinatario_telefone_ddd ? [{
          ddd: values.destinatario_telefone_ddd,
          numero: values.destinatario_telefone_numero,
        }] : [],
        email: values.destinatario_email,
        lat: values.destinatario_lat ? parseFloat(values.destinatario_lat) : null,
        lng: values.destinatario_lng ? parseFloat(values.destinatario_lng) : null,
      },
      
      // Campos planos para o backend (destinatario_lat e destinatario_lng)
      destinatario_lat: values.destinatario_lat && values.destinatario_lat !== '' ? parseFloat(values.destinatario_lat) : null,
      destinatario_lng: values.destinatario_lng && values.destinatario_lng !== '' ? parseFloat(values.destinatario_lng) : null,
      
      tomador: {
        tipo: values.tomador_tipo,
        cpf_cnpj: removerMascaraCpfCnpj(values.tomador_cpf_cnpj),
        nome_razao_social: values.tomador_nome_razao_social,
        inscricao_estadual: values.tomador_inscricao_estadual,
        endereco: {
          logradouro: values.tomador_logradouro,
          numero: values.tomador_numero,
          complemento: values.tomador_complemento,
          bairro: values.tomador_bairro,
          cep: removerMascaraCep(values.tomador_cep),
          uf: values.tomador_uf,
          municipio: {
            codigo_ibge: values.tomador_municipio_codigo,
            descricao: values.tomador_municipio_descricao,
          },
        },
        telefones: values.tomador_telefone_ddd ? [{
          ddd: values.tomador_telefone_ddd,
          numero: values.tomador_telefone_numero,
        }] : [],
        email: values.tomador_email,
      },
      
      transporte: {
        modal: values.modal,
        valor_servico: values.valor_servico ? parseFloat(values.valor_servico.toString().replace(/[^\d,.-]/g, '').replace(',', '.')) || 0 : 0,
        valor_receber: values.valor_receber ? parseFloat(values.valor_receber.toString().replace(/[^\d,.-]/g, '').replace(',', '.')) || 0 : 0,
        valor_carga: values.valor_carga ? parseFloat(values.valor_carga.toString().replace(/[^\d,.-]/g, '').replace(',', '.')) || 0 : 0,
        produtos_predominantes: values.produtos_predominantes,
        outras_caracteristicas: values.outras_caracteristicas,
      },
      
      veiculo: {
        placa: values.veiculo_placa,
        renavam: values.veiculo_renavam,
        uf: values.veiculo_uf,
        proprietario: {
          cpf_cnpj: removerMascaraCpfCnpj(values.veiculo_proprietario_cpf_cnpj),
          nome_razao_social: values.veiculo_proprietario_nome,
          inscricao_estadual: values.veiculo_proprietario_inscricao_estadual,
          uf: values.veiculo_proprietario_uf,
        },
      },
      
      informacoes_adicionais: values.informacoes_adicionais,
      observacoes: values.observacoes,
      chave_nfe: values.chave_nfe || '',
      
      motorista_id: values.motorista_id,
      motorista_nome: values.motorista_nome,
      motorista_cpf: removerMascaraCpfCnpj(values.motorista_cpf),
      motorista_telefone_ddd: values.motorista_telefone_ddd,
      motorista_telefone_numero: values.motorista_telefone_numero,

      // Embarcador
      embarcador_id: values.embarcador_id || null,

    };

    // Adicionar empresa_id se houver empresa selecionada
    try {
      const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany') || 'null');
      if (selectedCompany?.id) {
        dadosCompletos.empresa_id = selectedCompany.id;
      }
    } catch (error) {
      console.error('Erro ao obter empresa selecionada:', error);
    }

    const callback = () => {
      // Recarregar lista de viagens antes de redirecionar
      dispatch(TripActions.getViagensRequest());
      // Aguardar um pouco para garantir que a lista foi carregada
      setTimeout(() => {
        history.push('/trip');
      }, 500);
    };

    if (isEdit) {
      dispatch(TripActions.updateViagemRequest(id, dadosCompletos, callback));
    } else {
      dispatch(TripActions.addViagemRequest(dadosCompletos, callback));
    }
  };

  const tabs = [
    'Dados do CT-e',
    'Emitente',
    'Remetente',
    'Destinatário',
    'Tomador',
    'Transporte',
    'Motorista',
    'Veículo',
  ];

  // Importar o conteúdo completo do formulário do ModalViagemCompleto
  // Por questão de espaço, vou criar um componente separado ou reutilizar
  // Por enquanto, vou importar diretamente o conteúdo do ModalViagemCompleto
  return (
    <div style={{ padding: '20px', width: '100%' }}>
      <Paper
        title={isEdit ? 'Editar Viagem' : 'Nova Viagem'}
        icon={Images.IconPaper}
        rightContent={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Button
              label="Voltar"
              onClick={() => history.push('/trip')}
              type="button"
              margin="0 10px 0 0"
            />
            <Button
              label={isEdit ? 'Salvar' : 'Criar'}
              onClick={() => {
                if (formikRef.current) {
                  formikRef.current.handleSubmit();
                }
              }}
              type="button"
            />
          </div>
        }
      >
        <Container style={{ padding: '20px', maxHeight: 'none', overflow: 'visible', width: '100%', maxWidth: '100%' }}>
          {/* Tabs */}
          <div style={{ display: 'flex', marginBottom: '20px', borderBottom: '1px solid #ddd' }}>
            {tabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '5px',
                  background: activeTab === index ? '#122D58' : 'transparent',
                  color: activeTab === index ? 'white' : '#122D58',
                  cursor: 'pointer',
                  borderBottom: activeTab === index ? '2px solid #122D58' : 'none',
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          <Formik 
            innerRef={formikRef}
            initialValues={initialValues} 
            onSubmit={handleSubmit} 
            enableReinitialize
          >
            {({ values, handleChange, handleSubmit: formikHandleSubmit, errors, touched, setFieldValue, setValues }) => (
              <form onSubmit={formikHandleSubmit}>
                <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <ImportarXmlViagem
                    onXmlProcessed={() => {
                      // Opcional: fazer algo após processar o XML
                    }}
                    setFieldValue={setFieldValue}
                  />
                  <span style={{ fontSize: '14px', color: '#666', marginLeft: '10px' }}>
                    Selecione um arquivo XML para preencher automaticamente os dados da viagem
                  </span>
                </div>
                <ViagemForm
                  values={values}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  setValues={setValues}
                  errors={errors}
                  touched={touched}
                  activeTab={activeTab}
                  truckers={truckers}
                  providers={providers}
                  embarcadores={embarcadores}
                />
              </form>
            )}
          </Formik>
        </Container>
      </Paper>
      
      {/* Modal (padrão da Carga) para cadastrar tomador: abre a tela de inclusão de cliente */}
      <ModalClienteNaoCadastrado
        open={showErrorModal}
        message={errorMessage}
        cpfCnpj={cpfCnpjTomador}
        dadosTomador={dadosTomador}
        onClose={() => {
          setShowErrorModal(false);
          setErrorMessage('');
          setCpfCnpjTomador('');
          setDadosTomador(null);
        }}
        onCancel={() => {
          setShowErrorModal(false);
          setErrorMessage('');
          setCpfCnpjTomador('');
          setDadosTomador(null);
        }}
      />
    </div>
  );
}

export default CreateTrip;

